import * as React from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import UploadIcon from '@mui/icons-material/Upload';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useQuery } from '../hooks/useQuery';
import { useSnackbar } from '../hooks/useSnackbar';
import CustomizedDialogs from '../components/ModalAlert';
import DemaisArquivosItem from '../components/DemaisArquivosItem';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { handleHttpError, isFileBig } from '../utils';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useTheme } from '@mui/material/styles';

const TIPO_ARQUIVOS_PERMITIDOS = ['pdf', 'xls', 'xlsx'];

export const PagamentoAnexoPage = ({
  activeStep,
  setActiveStep,
  steps,
  pagamentoDiretoId,
}) => {
  const [file, setFile] = React.useState(null);
  const [isFetchingFiles, setIsFetchingFiles] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [filesUploaded, setFilesUploaded] = React.useState([]);
  const [messageError, setMessageError] = React.useState('');
  const [showDialogError, setShowDialogError] = React.useState(false);
  const [show, setShow] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [type, setType] = React.useState('');
  const { user } = useAuth();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const theme = useTheme();
  const [validate, setValidate] = React.useState(true);

  const {
    listarArquivosPagamentoDireto,
    enviarArquivosPagamentoDireto,
    deletarArquivoPagamentoDireto,
    enviarPagamentoDiretoFluig,
    enviarPagamentoDiretoDataSul,
    getPagamentoDireto,
  } = useQuery();

  React.useEffect(() => {
    fetchFiles();
    setFile(null);
  }, [isFetchingFiles]);

  React.useEffect(() => {
    if (filesUploaded && filesUploaded.length > 0) {
      setValidate(false);
    } else {
      setValidate(true);
    }
  }, [filesUploaded]);

  const fetchFiles = async () => {
    const response = await listarArquivosPagamentoDireto(
      pagamentoDiretoId,
      'adicional',
    );
    setFilesUploaded(response.data.data);
    setIsFetchingFiles(false);
  };

  const temArquivoSemDescricao = () => {
    return (
      filesUploaded.filter(
        ({ descricao }) => descricao === null || descricao === '',
      ).length > 0
    );
  };

  const sendFile = async () => {
    setIsUpdating(true);
    const formData = new FormData();
    formData.append('arquivo', file);
    formData.append('tipo', 'adicional');
    try {
      await enviarArquivosPagamentoDireto(pagamentoDiretoId, formData);
      setIsFetchingFiles(!isFetchingFiles);
    } catch (error) {
      snackbar.showSnackbar(handleHttpError(error));
    } finally {
      setIsUpdating(false);
    }
  };

  React.useEffect(() => {
    if (file) {
      sendFile();
    }
  }, [file]);

  const handleButtonOk = () => {
    setShow(false);

    navigate(`/dashboard/pagamento-direto`);
  };

  const handleNext = async () => {
    if (temArquivoSemDescricao()) {
      snackbar.showSnackbar('Preencha a descrição dos arquivos');
      return;
    }

    setLoading(true);
    let responseDatasul = await enviarPagamentoDiretoDataSul(pagamentoDiretoId);
    let resultDatasul = await responseDatasul.json();

    if (resultDatasul && resultDatasul.success) {
      let response = await enviarPagamentoDiretoFluig(
        pagamentoDiretoId,
        user.nome_usuario,
        user.role_id == 6 ? 'S' : 'N',
        user.user_id,
      );
      let resultFluig = await response.json();

      if (
        resultFluig &&
        resultFluig.data &&
        resultFluig.data.response &&
        resultFluig.data.response.item &&
        resultFluig.data.response.item.item &&
        resultFluig.data.response.item.item[0] &&
        resultFluig.data.response.item.item[0] == 'ERROR'
      ) {
        setMessageError(resultFluig.data.response.item.item[1]);
        setShowDialogError(true);
        setLoading(false);
      } else {
        setMessage('Pagamento direto enviado com sucesso.');
        setType('success');
        setShow(true);
        setLoading(false);
      }
    } else {
      setMessageError(
        resultDatasul.message
          ? resultDatasul.message
          : 'Ocorreu um erro inesperado ao enviar o pagamento direto. Tente novamente mais tarde. Se o erro persistir entre em contato com o administrador do sistema.',
      );
      setShowDialogError(true);
      setLoading(false);
    }
  };

  const onChangeFile = (event) => {
    const files = [...event.target.files];

    if (files.length > 0) {
      const file = files[0];
      const extension = file.name.split('.').pop();

      if (!TIPO_ARQUIVOS_PERMITIDOS.includes(extension)) {
        snackbar.showSnackbar(
          'Tipo de arquivo inválido. Somente ' +
            TIPO_ARQUIVOS_PERMITIDOS.join(','),
        );
        setFile(null);
        return;
      }

      if (isFileBig(file.size)) {
        snackbar.showSnackbar('Tamanho de arquivo inválido. Máximo é 10MB');
        return;
      }

      setFile(file);

      event.target.value = null;
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onPressDelete = async (fileUploaded, index) => {
    const confirm = await window.confirm('Deseja realmente excluir o arquivo?');

    if (confirm) {
      setIsUpdating(true);
      await deletarArquivoPagamentoDireto(fileUploaded.id);
      setIsFetchingFiles(!isFetchingFiles);
      setIsUpdating(false);
    }
  };

  return (
    <Card
      variant="outlined"
      style={{
        borderTop: `8px solid ${theme.palette.primary.light}`,
        marginTop: '2rem',
      }}
    >
      <CardContent>
        <CustomizedDialogs
          content={messageError}
          btnTxt={'Ok'}
          type="error"
          onClick={() => {
            setShowDialogError(false);
          }}
          handleClose={setShowDialogError}
          visible={showDialogError}
        />
        <CustomizedDialogs
          content={message}
          btnTxt={'Ok'}
          type={type}
          onClick={handleButtonOk}
          handleClose={setShow}
          visible={show}
        />

        <Grid style={{ marginTop: 10 }} container spacing={2}>
          <Grid item sm={12}>
            <p style={{ color: '#0e72b8', marginTop: 0, fontWeight: 'bold' }}>
              Carregar anexos
            </p>
          </Grid>

          <Grid item sm={10}>
            <TextField
              disabled
              style={{ width: '100%' }}
              id="outlined-basic"
              value={(file && file.name) || ''}
              placeholder="Importar arquivos"
              variant="outlined"
            />
          </Grid>

          <Grid item sm={2}>
            <Button
              style={{ width: '100%', height: 40 }}
              variant="contained"
              component="label"
            >
              <UploadIcon />
              Importar
              <input
                onChange={onChangeFile}
                hidden
                accept={TIPO_ARQUIVOS_PERMITIDOS.join(',')}
                type="file"
              />
            </Button>
          </Grid>
        </Grid>

        <br />

        {filesUploaded.map((item, index) => (
          <DemaisArquivosItem
            key={`${index}-demais-arquivos-item`}
            item={item}
            onPressDelete={onPressDelete}
            atualizarLista={fetchFiles}
            isPagamentoDireto={true}
            setValidate={setValidate}
          />
        ))}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            pt: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            onClick={handleNext}
            variant="contained"
            disabled={validate || temArquivoSemDescricao()}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              {loading ? (
                <CircularProgress color={'inherit'} size={25} />
              ) : (
                <span>Enviar</span>
              )}
            </Box>
            <ArrowRightAltIcon color="primary" />
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
